import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategories, useBrands } from '../../hooks/useProducts.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useCart } from '../../hooks/useCart.js';

const Sidebar = React.memo(() => {
  const navigate = useNavigate();
  const { isAuthenticated, isCustomer, canProceedToShopping } = useAuth();
  
  // FIXED: Memoize cart count to prevent re-renders
  const { getCartItemCount, canUseCart } = useCart();
  const cartItemCount = React.useMemo(() => {
    return canUseCart ? getCartItemCount() : 0;
  }, [canUseCart, getCartItemCount]);
  
  // FIXED: Stable options for hooks
  const categoriesOptions = React.useMemo(() => ({ 
    enabled: isAuthenticated 
  }), [isAuthenticated]);
  
  const brandsOptions = React.useMemo(() => ({ 
    enabled: isAuthenticated 
  }), [isAuthenticated]);
  
  // Fetch data with stable options
  const { 
    data: categories = [], 
    isLoading: categoriesLoading, 
    error: categoriesError 
  } = useCategories(categoriesOptions);

  const { 
    data: brands = [], 
    isLoading: brandsLoading, 
    error: brandsError 
  } = useBrands(brandsOptions);

  // FIXED: Stable navigation handlers
  const handleCategoryClick = useCallback((category) => {
    if (canProceedToShopping()) {
      navigate(`/categories/${category.slug}`);
    }
  }, [canProceedToShopping, navigate]);

  const handleBrandClick = useCallback((brand) => {
    if (canProceedToShopping()) {
      navigate(`/brands/${brand.slug}`);
    }
  }, [canProceedToShopping, navigate]);

  const handleQuickActionClick = useCallback((path) => {
    if (path.includes('/account') || canProceedToShopping()) {
      navigate(path);
    }
  }, [canProceedToShopping, navigate]);

  // Loading state
  if (categoriesLoading || brandsLoading) {
    return (
      <aside className="stash-sidebar">
        <div className="stash-sidebar__loading">
          <div className="stash-loading__spinner">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <p className="stash-sidebar__loading-text">Loading navigation...</p>
        </div>
      </aside>
    );
  }

  // Error state
  if (categoriesError || brandsError) {
    return (
      <aside className="stash-sidebar">
        <div className="stash-sidebar__error">
          <div className="stash-sidebar__error-icon">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <p className="stash-sidebar__error-text">Unable to load navigation data</p>
          <button 
            className="btn btn-outline-stash-primary btn-sm"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="stash-sidebar">
      {/* Popular Categories Widget */}
      <section className="stash-sidebar__widget">
        <h3 className="stash-sidebar__widget-title">
          Popular Categories
        </h3>
        <ul className="stash-sidebar__widget-list">
          {categories.length > 0 ? (
            categories.slice(0, 8).map((category) => (
              <li key={category.id} className="stash-sidebar__widget-item">
                <button
                  className={`stash-sidebar__widget-link ${
                    !canProceedToShopping() ? 'stash-sidebar__widget-link--disabled' : ''
                  }`}
                  onClick={() => handleCategoryClick(category)}
                  disabled={!canProceedToShopping()}
                >
                  <span className="stash-sidebar__widget-link-text">
                    {category.name}
                  </span>
                  {category.productCount > 0 && (
                    <span className="stash-sidebar__widget-count">
                      ({category.productCount})
                    </span>
                  )}
                </button>
              </li>
            ))
          ) : (
            <li className="stash-sidebar__widget-item stash-sidebar__widget-item--empty">
              <span className="stash-sidebar__widget-empty">No categories available</span>
            </li>
          )}
        </ul>
      </section>

      {/* Popular Brands Widget */}
      <section className="stash-sidebar__widget">
        <h3 className="stash-sidebar__widget-title">
          Popular Brands
        </h3>
        <ul className="stash-sidebar__widget-list">
          {brands.length > 0 ? (
            brands.slice(0, 8).map((brand) => (
              <li key={brand.id} className="stash-sidebar__widget-item">
                <button
                  className={`stash-sidebar__widget-link ${
                    !canProceedToShopping() ? 'stash-sidebar__widget-link--disabled' : ''
                  }`}
                  onClick={() => handleBrandClick(brand)}
                  disabled={!canProceedToShopping()}
                >
                  <span className="stash-sidebar__widget-link-text">
                    {brand.name}
                  </span>
                </button>
              </li>
            ))
          ) : (
            <li className="stash-sidebar__widget-item stash-sidebar__widget-item--empty">
              <span className="stash-sidebar__widget-empty">No brands available</span>
            </li>
          )}
        </ul>
      </section>

      {/* Quick Actions Widget */}
      <section className="stash-sidebar__widget">
        <h3 className="stash-sidebar__widget-title">
          Quick Actions
        </h3>
        <ul className="stash-sidebar__widget-list">
          {isAuthenticated ? (
            <>
              <li className="stash-sidebar__widget-item">
                <button
                  className={`stash-sidebar__widget-link stash-sidebar__widget-link--action ${
                    !canProceedToShopping() ? 'stash-sidebar__widget-link--disabled' : ''
                  }`}
                  onClick={() => handleQuickActionClick('/quick-order')}
                  disabled={!canProceedToShopping()}
                >
                  <span className="stash-sidebar__widget-link-icon">
                    <i className="bi bi-lightning"></i>
                  </span>
                  <span className="stash-sidebar__widget-link-text">
                    Quick Order
                  </span>
                </button>
              </li>
              <li className="stash-sidebar__widget-item">
                <button
                  className="stash-sidebar__widget-link stash-sidebar__widget-link--action"
                  onClick={() => handleQuickActionClick('/orders')}
                >
                  <span className="stash-sidebar__widget-link-icon">
                    <i className="bi bi-clock-rotate-left"></i>
                  </span>
                  <span className="stash-sidebar__widget-link-text">
                    Order History
                  </span>
                </button>
              </li>
              <li className="stash-sidebar__widget-item">
                <button
                  className={`stash-sidebar__widget-link stash-sidebar__widget-link--action ${
                    !canUseCart ? 'stash-sidebar__widget-link--disabled' : ''
                  }`}
                  onClick={() => handleQuickActionClick('/checkout')}
                  disabled={!canUseCart}
                >
                  <span className="stash-sidebar__widget-link-icon">
                    <i className="bi bi-cart"></i>
                  </span>
                  <span className="stash-sidebar__widget-link-text">
                    View Cart
                  </span>
                  {canUseCart && cartItemCount > 0 && (
                    <span className="stash-sidebar__widget-badge">
                      {cartItemCount}
                    </span>
                  )}
                </button>
              </li>
            </>
          ) : (
            <li className="stash-sidebar__widget-item">
              <button 
                className="stash-sidebar__widget-link stash-sidebar__widget-link--primary"
                onClick={() => navigate('/login')}
              >
                <span className="stash-sidebar__widget-link-icon">
                  <i className="bi bi-box-arrow-in-right"></i>
                </span>
                <span className="stash-sidebar__widget-link-text">
                  Sign In
                </span>
              </button>
            </li>
          )}
        </ul>
      </section>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;